/* Native hero controls with deterministic setup and teardown in the theme editor. */
(() => {
  "use strict";
  if (customElements.get("gns-hero")) return;
  const motion = () =>
    matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";

  function initSalesCount(root) {
    const count = root.querySelector("[data-gns-sold]");
    if (!count || motion() === "auto" || !("IntersectionObserver" in window))
      return;
    const finalText = count.textContent;
    const parts = finalText.trim().match(/^(\+?)(\d{1,3}(?:,\d{3})+|\d+)$/);
    if (!parts) return;
    const target = Number(parts[2].replaceAll(",", ""));
    if (!Number.isSafeInteger(target) || target <= 0) return;
    let frame = 0;
    let started = false;
    const observer = new IntersectionObserver((entries) => {
      if (started || !entries.some((entry) => entry.isIntersecting)) return;
      started = true;
      observer.disconnect();
      let start;
      const draw = (time) => {
        start ??= time;
        const progress = Math.min(1, (time - start) / 850);
        const value = Math.round(target * (1 - Math.pow(1 - progress, 3)));
        count.textContent = progress === 1
          ? finalText
          : parts[1] + value.toLocaleString("en-US");
        if (progress < 1) frame = requestAnimationFrame(draw);
      };
      frame = requestAnimationFrame(draw);
    });
    observer.observe(count);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      count.textContent = finalText;
    };
  }

  function formatDate(date) {
    const month = new Intl.DateTimeFormat("en-US", { month: "long" }).format(
      date,
    );
    const day = date.getDate();
    const mod100 = day % 100;
    const suffix =
      mod100 >= 11 && mod100 <= 13
        ? "th"
        : { 1: "st", 2: "nd", 3: "rd" }[day % 10] || "th";
    return `${month} ${day}${suffix}`;
  }

  function dateWithOffset(baseDate, offset) {
    const date = new Date(baseDate);
    date.setDate(date.getDate() + offset);
    return date;
  }

  function initBatch(batch) {
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const soldOffset = Number(batch.dataset.soldOffset || -1);
    const reservedOffset = Number(batch.dataset.reservedOffset || 2);
    const formulatingOffset = Number(batch.dataset.formulatingOffset || 9);
    const reservedMin = Number(batch.dataset.reservedMin || 87);
    const percentage = Number(batch.dataset.reservedPercentage ?? reservedMin);

    const soldDate = batch.querySelector("[data-gno-batch-sold-date]");
    const reservedDate = batch.querySelector("[data-gno-batch-reserved-date]");
    const formulatingDate = batch.querySelector(
      "[data-gno-batch-formulating-date]",
    );
    const reservedStatus = batch.querySelector(
      "[data-gno-batch-reserved-status]",
    );
    const fill = batch.querySelector("[data-gno-batch-fill]");

    if (soldDate)
      soldDate.textContent =
        batch.dataset.soldDate || formatDate(dateWithOffset(today, soldOffset));
    if (reservedDate)
      reservedDate.textContent =
        batch.dataset.reservedDate ||
        formatDate(dateWithOffset(today, reservedOffset));
    if (formulatingDate)
      formulatingDate.textContent =
        batch.dataset.formulatingDate ||
        formatDate(dateWithOffset(today, formulatingOffset));
    if (reservedStatus)
      reservedStatus.textContent = `${percentage}% ${batch.dataset.reservedLabel || "RESERVED"}`;
    if (fill) fill.style.width = `${percentage}%`;
  }

  function initGallery(root, listen) {
    const gallery = root.querySelector("[data-gno-gallery]");
    if (!gallery) return;
    const stage = gallery.querySelector("[data-gno-stage]");
    const track = gallery.querySelector("[data-gno-gallery-track]");
    const slides = [...gallery.querySelectorAll("[data-gno-slide]")];
    const thumbs = [...gallery.querySelectorAll("[data-gno-thumb]")];
    const status = gallery.querySelector("[data-gno-status]");
    if (!track || slides.length < 2) return;

    let activeIndex = Math.max(
      0,
      slides.findIndex((slide) => slide.classList.contains("is-active")),
    );
    let requestedIndex = null;
    let width = stage.clientWidth;
    let endTimer = 0;
    let touching = false;
    const copies = [slides.at(-1), slides[0]].map((slide) => {
      const copy = slide.cloneNode(true);
      copy.removeAttribute("data-gno-slide");
      copy.classList.remove("is-active");
      copy.setAttribute("aria-hidden", "true");
      copy.querySelectorAll("img").forEach((image) => {
        image.loading = "lazy";
        image.fetchPriority = "auto";
      });
      return copy;
    });
    track.prepend(copies[0]);
    track.append(copies[1]);
    const wrap = (index) => (index + slides.length) % slides.length;

    function update(index, focusThumb = false) {
      activeIndex = wrap(index);
      const thumb = thumbs[activeIndex];
      const rail = thumb?.parentElement;
      // Read geometry together before changing slide and thumbnail styles.
      const thumbLeft = thumb
        ? thumb.offsetLeft -
          rail.offsetLeft -
          (rail.clientWidth - thumb.clientWidth) / 2
        : 0;
      slides.forEach((slide, i) => {
        slide.classList.toggle("is-active", i === activeIndex);
        slide.setAttribute("aria-hidden", String(i !== activeIndex));
      });
      thumbs.forEach((thumb, i) => {
        thumb.classList.toggle("is-active", i === activeIndex);
        thumb.setAttribute("aria-current", String(i === activeIndex));
      });
      if (thumb) {
        rail.scrollTo({
          left: thumbLeft,
          behavior: motion(),
        });
        if (focusThumb) thumb.focus({ preventScroll: true });
      }
      if (status)
        status.textContent = `${status.dataset.slideLabel || "Slide"} ${activeIndex + 1} ${status.dataset.slideOfLabel || "of"} ${slides.length}`;
    }

    function scrollTo(position, animate = false) {
      stage.scrollTo({
        left: position * width,
        behavior: animate && motion() === "smooth" ? "smooth" : "instant",
      });
    }

    function finish() {
      clearTimeout(endTimer);
      if (!width || touching) return;
      const position = Math.max(
        0,
        Math.min(slides.length + 1, Math.round(stage.scrollLeft / width)),
      );
      const index = wrap(position - 1);
      requestedIndex = null;
      if (index !== activeIndex) update(index);
      // Identical edge copies preserve looping without intercepting any swipe.
      if (position === 0 || position === slides.length + 1) scrollTo(index + 1);
    }

    function select(index, focusThumb = false) {
      const wrapped = wrap(index);
      // Normalize an unfinished edge transition before another rapid arrow click.
      if (requestedIndex !== null) scrollTo(activeIndex + 1);
      requestedIndex = wrapped;
      update(wrapped, focusThumb);
      scrollTo(
        index < 0
          ? 0
          : index >= slides.length
            ? slides.length + 1
            : wrapped + 1,
        true,
      );
    }

    update(activeIndex);
    scrollTo(activeIndex + 1);
    thumbs.forEach((thumb, index) =>
      listen(thumb, "click", () => select(index)),
    );
    listen(gallery.querySelector("[data-gno-prev]"), "click", () =>
      select((requestedIndex ?? activeIndex) - 1),
    );
    listen(gallery.querySelector("[data-gno-next]"), "click", () =>
      select((requestedIndex ?? activeIndex) + 1),
    );
    listen(gallery, "keydown", (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      select(
        (requestedIndex ?? activeIndex) + (event.key === "ArrowRight" ? 1 : -1),
        true,
      );
    });
    const scheduleFinish = () => {
      clearTimeout(endTimer);
      endTimer = setTimeout(finish, 180);
    };
    listen(
      stage,
      "scroll",
      () => {
        if (width && requestedIndex === null) {
          const index = wrap(Math.round(stage.scrollLeft / width) - 1);
          if (index !== activeIndex) update(index);
        }
        if (!("onscrollend" in stage)) scheduleFinish();
      },
      { passive: true },
    );
    listen(stage, "scrollend", finish, { passive: true });
    // These observers never capture a pointer, prevent default, or move the track.
    listen(
      stage,
      "pointerdown",
      () => {
        touching = true;
        requestedIndex = null;
      },
      { passive: true },
    );
    const release = () => {
      if (touching) {
        touching = false;
        scheduleFinish();
      }
    };
    listen(window, "pointerup", release, { passive: true });
    listen(window, "pointercancel", release, { passive: true });
    const resize = () => {
      const nextWidth = stage.clientWidth;
      if (!nextWidth || nextWidth === width) return;
      width = nextWidth;
      requestedIndex = null;
      scrollTo(activeIndex + 1);
    };
    const observer =
      "ResizeObserver" in window ? new ResizeObserver(resize) : null;
    if (observer) observer.observe(stage);
    else listen(window, "resize", resize);
    return () => {
      clearTimeout(endTimer);
      observer?.disconnect();
      copies.forEach((copy) => copy.remove());
    };
  }

  function initNutrition(root, listen) {
    const dialog = root.querySelector("[data-gno-nutrition-dialog]");
    const trigger = root.querySelector("[data-gno-nutrition-open]");
    const closeButtons = dialog?.querySelectorAll("[data-gno-nutrition-close]") || [];
    if (!dialog || !trigger) return;

    const closeDialog = () => {
      if (typeof dialog.close === "function" && dialog.open) dialog.close();
      else dialog.removeAttribute("open");
    };

    listen(trigger, "click", () => {
      if (typeof dialog.showModal === "function") dialog.showModal();
      else dialog.setAttribute("open", "");
    });
    closeButtons.forEach((button) => listen(button, "click", closeDialog));
    listen(dialog, "close", () => trigger.focus({ preventScroll: true }));
    listen(dialog, "click", (event) => {
      if (event.target === dialog) closeDialog();
    });
  }

  function initReviewCarousel(carousel, listen) {
    const track = carousel.querySelector("[data-gno-review-track]");
    const slides = [...carousel.querySelectorAll("[data-gno-review-slide]")];
    const previous = carousel.querySelector("[data-gno-review-prev]");
    const next = carousel.querySelector("[data-gno-review-next]");
    const status = carousel.querySelector("[data-gno-review-status]");
    if (!track || slides.length < 2) return;

    let activeIndex = 0;
    let pointerStartX = null;
    let pointerStartY = null;

    function select(index) {
      activeIndex = (index + slides.length) % slides.length;
      track.style.transform = `translate3d(-${activeIndex * 100}%, 0, 0)`;
      slides.forEach((slide, slideIndex) => {
        slide.setAttribute("aria-hidden", String(slideIndex !== activeIndex));
      });
      if (status) {
        const reviewLabel = status.dataset.reviewLabel || "Review";
        const ofLabel = status.dataset.ofLabel || "of";
        status.textContent = `${reviewLabel} ${activeIndex + 1} ${ofLabel} ${slides.length}`;
      }
    }

    listen(previous, "click", () => select(activeIndex - 1));
    listen(next, "click", () => select(activeIndex + 1));

    listen(carousel, "keydown", (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      select(activeIndex + (event.key === "ArrowRight" ? 1 : -1));
    });

    listen(
      carousel,
      "pointerdown",
      (event) => {
        if (event.pointerType === "mouse") return;
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { passive: true },
    );

    listen(
      carousel,
      "pointerup",
      (event) => {
        if (event.pointerType === "mouse" || pointerStartX === null) return;
        const distance = event.clientX - pointerStartX;
        const vertical = event.clientY - pointerStartY;
        pointerStartX = null;
        if (Math.abs(distance) >= 40 && Math.abs(distance) > Math.abs(vertical))
          select(activeIndex + (distance < 0 ? 1 : -1));
      },
      { passive: true },
    );

    listen(
      carousel,
      "pointercancel",
      () => {
        pointerStartX = null;
      },
      { passive: true },
    );
  }

  function initCalculator(calculator, listen) {
    const weightInput = calculator.querySelector("[data-gno-weight]");
    const heightInput = calculator.querySelector("[data-gno-height]");
    const weightOutput = calculator.querySelector("[data-gno-weight-output]");
    const heightOutput = calculator.querySelector("[data-gno-height-output]");
    const bmiOutput = calculator.querySelector("[data-gno-bmi]");
    const lossOutput = calculator.querySelector("[data-gno-loss]");
    const warning = calculator.querySelector("[data-gno-warning]");
    const warningTitle = calculator.querySelector("[data-gno-warning-title]");
    const warningMessage = calculator.querySelector(
      "[data-gno-warning-message]",
    );
    const moreButton = calculator.querySelector("[data-gno-more]");
    const benefits = calculator.querySelector("[data-gno-benefits]");
    if (!weightInput || !heightInput) return;

    const messages = {};
    calculator.querySelectorAll("[data-gno-message]").forEach((message) => {
      messages[message.dataset.gnoMessage] = {
        title: message.dataset.title || "",
        body: message.textContent.trim(),
      };
    });

    function update() {
      const weight = Number(weightInput.value);
      const height = Number(heightInput.value);
      const bmi = Math.round((703 * weight) / (height * height));
      let percentage = Number(calculator.dataset.lossUnder || 8);
      let category = "normal";
      let warningClass = "normal";

      if (bmi < 18.5) {
        category = "underweight";
        warningClass = "underweight";
      } else if (bmi >= 35) {
        percentage = Number(calculator.dataset.lossObeseTwo || 14);
        category = "obese-2";
        warningClass = "obese";
      } else if (bmi >= 30) {
        percentage = Number(calculator.dataset.lossObeseOne || 12);
        category = "obese-1";
        warningClass = "obese";
      } else if (bmi >= 25) {
        percentage = Number(calculator.dataset.lossOverweight || 10);
        category = "overweight";
        warningClass = "overweight";
      }

      const feet = Math.floor(height / 12);
      const inches = height % 12;
      if (weightOutput)
        weightOutput.textContent = `${weight} ${calculator.dataset.pounds || "lbs"}`;
      if (heightOutput)
        heightOutput.textContent = `${feet}${calculator.dataset.feet || "ft"} ${inches}${calculator.dataset.inches || "in"}`;
      if (bmiOutput) bmiOutput.textContent = String(bmi);
      if (lossOutput)
        lossOutput.textContent = `${Math.round((weight * percentage) / 100)}${calculator.dataset.pounds || "lbs"}`;

      const message = messages[category];
      if (warning)
        warning.className = `gns-hero__bmi-warning is-${warningClass}`;
      if (warningTitle && message) warningTitle.textContent = message.title;
      if (warningMessage && message) warningMessage.textContent = message.body;
    }

    listen(weightInput, "input", update);
    listen(heightInput, "input", update);
    update();

    listen(moreButton, "click", () => {
      if (!benefits) return;
      const expanded = moreButton.getAttribute("aria-expanded") === "true";
      benefits.hidden = expanded;
      moreButton.setAttribute("aria-expanded", String(!expanded));
      moreButton.textContent = expanded
        ? moreButton.dataset.moreLabel || "View More"
        : moreButton.dataset.lessLabel || "View Less";
    });
  }

  class Hero extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const listen = (target, type, handler, options = {}) =>
        target?.addEventListener(type, handler, {
          ...options,
          signal: this.controller.signal,
        });
      this.dataset.gnsInput = "pointer";
      listen(document, "pointerdown", () => { this.dataset.gnsInput = "pointer"; }, { capture: true });
      listen(document, "keydown", (event) => {
        if (["Tab", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) this.dataset.gnsInput = "keyboard";
      }, { capture: true });
      this.cleanupGallery = initGallery(this, listen);
      this.cleanupSalesCount = initSalesCount(this);
      initNutrition(this, listen);
      this.querySelectorAll("[data-gno-batch]").forEach(initBatch);
      this.querySelectorAll("[data-gno-review-carousel]").forEach((carousel) =>
        initReviewCarousel(carousel, listen),
      );
      this.querySelectorAll("[data-gno-calculator]").forEach((calculator) =>
        initCalculator(calculator, listen),
      );
      const sticky = this.querySelector("[data-gno-sticky]");
      const offer = this.querySelector("[data-gno-offer]");
      if (sticky && offer) {
        const updateSticky = () => {
          this.stickyFrame = 0;
          sticky.hidden = offer.getBoundingClientRect().bottom > 0;
        };
        const scheduleSticky = () => {
          if (!this.stickyFrame)
            this.stickyFrame = requestAnimationFrame(updateSticky);
        };
        // A direct anchor jump can skip an IntersectionObserver's visible state.
        listen(window, "scroll", scheduleSticky, { passive: true });
        listen(window, "resize", scheduleSticky);
        if ("ResizeObserver" in window) {
          this.observer = new ResizeObserver(scheduleSticky);
          this.observer.observe(offer);
        }
        updateSticky();
        listen(sticky, "click", () => {
          offer.scrollIntoView({ behavior: motion(), block: "center" });
          offer
            .querySelector('[data-gno-tier][aria-checked="true"]')
            ?.focus({ preventScroll: true });
        });
      }
      listen(document, "shopify:block:select", (event) => {
        if (!this.contains(event.target)) return;
        event.target.querySelector("[data-gno-tier]")?.click();
      });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      cancelAnimationFrame(this.stickyFrame);
      this.stickyFrame = 0;
      this.cleanupGallery?.();
      this.cleanupGallery = null;
      this.cleanupSalesCount?.();
      this.cleanupSalesCount = null;
      this.querySelectorAll("dialog[open]").forEach((dialog) => dialog.close());
    }
  }
  customElements.define("gns-hero", Hero);

  function closeMobileFooter(event) {
    if (!matchMedia("(max-width: 749px)").matches) return;
    const scope =
      event?.type === "shopify:section:load" ? event.target : document;
    scope.querySelectorAll(".nuora-footer-390__menu").forEach((menu) => {
      if (menu.dataset.gnoInitialized) return;
      menu.open = false;
      menu.dataset.gnoInitialized = "true";
    });
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", closeMobileFooter, {
      once: true,
    });
  } else closeMobileFooter();
  document.addEventListener("shopify:section:load", closeMobileFooter);
})();
